import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Search, Sparkles } from "lucide-react";
import { COUNTRIES, continentById } from "@/data/countries";
import { stampCountry } from "@/lib/progression";
import {
  getBornInSeen,
  setBornInSeen,
  getKidName,
  setKidName,
  getBirthCountryCode,
  setBirthCountryCode,
  getHeritageCodes,
  setHeritageCodes,
  ensureFirstVisitDay,
} from "@/lib/kidProfile";
import { getAgeSeen } from "@/lib/ageGroup";
import Flag from "@/components/Flag";

/**
 * One-time "Born in" personalization step.
 *
 * Shown automatically after the AgeSelector has been dismissed (we wait for
 * the age-seen flag to flip). Parents can enter the child's first name and
 * pick their country of birth, OR skip entirely.
 *
 * On submit:
 *   • Saves kid name + birth country to localStorage.
 *   • Pre-stamps the birth country in the Passport via `stampCountry()`
 *     so the very first session already has a meaningful achievement.
 *   • Marks "first visit day" so Home can swap in the birth country as the
 *     Country of the Day for today only.
 *   • Confetti-style toast: "Welcome [Name]! [Country] is your first stamp 🌟"
 *
 * On skip: just sets the `we_born_in_seen` flag so the modal never auto-opens
 * again. Parents can re-trigger via window event `we-open-born-in`.
 */
export default function BornInPicker() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [query, setQuery] = useState("");
  const [selectedCode, setSelectedCode] = useState("");
  const [heritage, setHeritage] = useState([]); // up to 3 heritage country codes

  // NOTE: This picker no longer auto-opens on first visit. The name/country
  // step is now merged into <AgeSelector /> as step 2, so parents only see
  // ONE modal on first launch. This component still exists to let parents
  // *edit* their kid's profile (name, primary country, heritage) via the
  // "we-open-born-in" imperative event fired from the menu's
  // "Change my country" pill.

  // Allow imperative re-open (e.g. from the Parents page or the drawer
  // "Change my country" pill). When re-opened, pre-fill the form with the
  // previously-saved values so parents can edit instead of re-entering.
  useEffect(() => {
    const onOpen = () => {
      setName(getKidName());
      setSelectedCode(getBirthCountryCode());
      setHeritage(getHeritageCodes());
      setQuery("");
      setOpen(true);
    };
    window.addEventListener("we-open-born-in", onOpen);
    return () => window.removeEventListener("we-open-born-in", onOpen);
  }, []);

  // Country search — filters by name OR code as the parent types.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES.slice(0, 60); // cap initial render for snappy paint
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        (c.capital || "").toLowerCase().includes(q),
    ).slice(0, 60);
  }, [query]);

  const selected = useMemo(
    () => COUNTRIES.find((c) => c.code === selectedCode) || null,
    [selectedCode],
  );

  const close = () => {
    setBornInSeen();
    ensureFirstVisitDay();
    setOpen(false);
  };

  // Dismiss when a tap lands outside the floating card. The picker is
  // deliberately non-blocking (aria-modal="false"), so we listen to
  // pointerdown at the document level and close only when the target
  // isn't inside the card. Attached only while `open` to avoid stray work.
  const cardRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => {
      const card = cardRef.current;
      if (!card) return;
      if (card.contains(e.target)) return;
      close();
    };
    // Wait a tick so the tap that OPENED the picker doesn't also close it.
    const t = setTimeout(() => {
      document.addEventListener("pointerdown", onOutside);
    }, 50);
    return () => {
      clearTimeout(t);
      document.removeEventListener("pointerdown", onOutside);
    };
  }, [open]);

  const submit = () => {
    if (name.trim()) setKidName(name);
    if (selectedCode) {
      setBirthCountryCode(selectedCode);
      // Pre-stamp the passport so the first session already feels rewarding.
      const country = COUNTRIES.find((c) => c.code === selectedCode);
      if (country) {
        stampCountry(country.code, {
          fact: country.funFact,
          continent: country.continent,
          home: true,      // marks the special "🏠 Home" stamp in Passport
        });
      }
      const niceName = name.trim() || "explorer";
      toast.success(
        `Welcome, ${niceName}! ${selected ? selected.name : "Your country"} is your first stamp 🌟`,
      );
    } else if (name.trim()) {
      toast(`Hi ${name.trim()}! Let's explore the world 🌍`, { icon: "👋" });
    }
    // Save + pre-stamp heritage countries (up to 3). Different key so the
    // Passport can style them distinctly from the primary "Home" stamp.
    const cleanHeritage = heritage.filter((code) => code && code !== selectedCode).slice(0, 3);
    setHeritageCodes(cleanHeritage);
    cleanHeritage.forEach((code) => {
      const c = COUNTRIES.find((x) => x.code === code);
      if (!c) return;
      stampCountry(c.code, { fact: c.funFact, continent: c.continent, heritage: true });
    });
    setBornInSeen();
    ensureFirstVisitDay();
    // Let Home (and any other listener) know the kid profile just changed,
    // so the Country of the Day can swap to the birth country without a
    // page reload.
    try {
      window.dispatchEvent(new Event("we-personalize-applied"));
    } catch { /* ignore */ }
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.92 }}
          transition={{ type: "spring", damping: 24, stiffness: 280 }}
          role="dialog"
          aria-modal="false"
          aria-label="Tell us about your child"
          data-testid="born-in-overlay"
          /* Bottom-right floating card. Non-blocking — the rest of the page
             stays scrollable and interactive. On phones we still pin to the
             bottom-right but let it span almost the full width so the flag
             grid stays usable. `bottom-20` keeps clear of the Emergent badge
             + Globy mascot which both sit in the bottom-right corner. */
          className="fixed z-[60] bottom-20 right-3 sm:bottom-20 sm:right-5 left-3 sm:left-auto w-auto sm:w-[360px] max-h-[72vh] sm:max-h-[78vh]"
        >
          <div
            ref={cardRef}
            data-testid="born-in-modal"
            className="relative rounded-2xl border-4 border-slate-900 bg-white shadow-[8px_8px_0_0_rgba(15,23,42,1)] overflow-hidden flex flex-col h-full max-h-[72vh] sm:max-h-[78vh]"
          >
            <button
              type="button"
              onClick={close}
              data-testid="born-in-skip"
              aria-label="Close"
              className="absolute top-2 right-2 z-10 h-8 w-8 inline-flex items-center justify-center rounded-full border-[3px] border-slate-900 bg-white shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-all"
            >
              <X size={16} strokeWidth={3} />
            </button>

            {/* Header — compact, fits the small floating footprint. */}
            <div className="bg-yellow-200 border-b-4 border-slate-900 px-4 pt-3 pb-2.5 flex-shrink-0">
              <div className="inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded-full bg-white border-[2.5px] border-slate-900 font-bold text-[9px] uppercase tracking-widest mb-1">
                <Sparkles size={9} strokeWidth={3} /> Optional · Skippable
              </div>
              <h2 className="font-heading text-lg font-bold leading-tight">
                Make it personal 🌟
              </h2>
              <p className="mt-0.5 text-[11px] font-bold text-slate-700 leading-snug">
                Your name and home country — we&apos;ll start your passport
                with your country!
              </p>
            </div>

            {/* Body — the ONLY scrollable region. */}
            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
              {/* Name field */}
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  Child&apos;s first name
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="born-in-name"
                  placeholder="e.g. Aisha"
                  maxLength={40}
                  autoComplete="off"
                  className="mt-1 w-full rounded-xl border-[3px] border-slate-900 bg-white px-3 py-1.5 font-bold text-sm focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-500/60 shadow-[0_3px_0_0_rgba(15,23,42,1)]"
                />
              </label>

              {/* Country picker */}
              <div className="mt-2.5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  Country of birth
                </span>

                {/* Currently selected pill */}
                {selected && (
                  <div
                    className="mt-1 flex items-center gap-2 rounded-xl border-[3px] border-slate-900 bg-emerald-100 p-1.5"
                    data-testid="born-in-selected"
                  >
                    <Flag
                      code={selected.code}
                      alt={`${selected.name} flag`}
                      width={40}
                      height={26}
                      className="h-6 w-10 object-cover rounded-md border-2 border-slate-900"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-xs truncate">
                        {selected.name}
                      </p>
                      <p className="text-[10px] font-bold text-slate-600 truncate">
                        {continentById(selected.continent)?.name}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedCode("")}
                      data-testid="born-in-clear"
                      className="text-[11px] font-bold underline text-slate-600 hover:text-slate-900"
                    >
                      Change
                    </button>
                  </div>
                )}

                {/* Search input */}
                {!selected && (
                  <div className="mt-1 relative">
                    <Search
                      size={14}
                      strokeWidth={3}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      data-testid="born-in-search"
                      placeholder="Search countries…"
                      className="w-full rounded-xl border-[3px] border-slate-900 bg-white pl-8 pr-3 py-1.5 font-bold text-xs focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-500/60 shadow-[0_3px_0_0_rgba(15,23,42,1)]"
                    />
                  </div>
                )}

                {/* Country grid — tighter so it fits in the floating card. */}
                {!selected && (
                  <div
                    className="mt-2 grid grid-cols-4 gap-1.5"
                    data-testid="born-in-grid"
                  >
                    {filtered.map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => setSelectedCode(c.code)}
                        data-testid={`born-in-country-${c.code}`}
                        className="group rounded-lg border-2 border-slate-900 bg-white p-0.5 shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_1px_0_0_rgba(15,23,42,1)] transition-all text-left"
                        aria-label={`Pick ${c.name}`}
                      >
                        <Flag
                          code={c.code}
                          alt=""
                          width={64}
                          height={38}
                          className="w-full aspect-[5/3] object-cover rounded border border-slate-900"
                          loading="lazy"
                        />
                        <p className="mt-0.5 text-[8px] font-bold text-center truncate leading-tight">
                          {c.name}
                        </p>
                      </button>
                    ))}
                    {!filtered.length && (
                      <p className="col-span-full text-center text-[11px] font-bold text-slate-500 py-3">
                        No countries match &ldquo;{query}&rdquo;
                      </p>
                    )}
                  </div>
                )}

                {/* Heritage multi-select (optional, up to 3) — shown once
                    the primary birth country has been picked. */}
                {selected && (
                  <div className="mt-3" data-testid="heritage-picker">
                    <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                      Family heritage (optional, up to 3)
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {heritage.map((code) => {
                        const c = COUNTRIES.find((x) => x.code === code);
                        if (!c) return null;
                        return (
                          <button
                            key={code}
                            type="button"
                            onClick={() => setHeritage((h) => h.filter((x) => x !== code))}
                            data-testid={`heritage-remove-${code}`}
                            className="inline-flex items-center gap-1 rounded-full border-[2px] border-slate-900 bg-white px-2 py-0.5 text-[11px] font-bold shadow-[0_2px_0_0_rgba(15,23,42,1)]"
                            title="Remove"
                          >
                            <Flag code={code} alt="" width={16} height={11} className="rounded-sm border border-slate-900" />
                            {c.name}
                            <X size={10} strokeWidth={4} />
                          </button>
                        );
                      })}
                    </div>
                    {heritage.length < 3 && (
                      <div className="mt-1 grid grid-cols-6 gap-1" data-testid="heritage-grid">
                        {filtered
                          .filter((c) => c.code !== selectedCode && !heritage.includes(c.code))
                          .slice(0, 18)
                          .map((c) => (
                            <button
                              key={c.code}
                              type="button"
                              onClick={() => setHeritage((h) => [...h, c.code].slice(0, 3))}
                              data-testid={`heritage-add-${c.code}`}
                              aria-label={`Add ${c.name} to heritage`}
                              className="rounded-md border-2 border-slate-900 bg-white p-0.5 shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_1px_0_0_rgba(15,23,42,1)] transition-all"
                            >
                              <Flag
                                code={c.code} alt="" width={40} height={24}
                                className="w-full aspect-[5/3] object-cover rounded-sm"
                                loading="lazy"
                              />
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <p className="mt-2 text-center text-[9px] font-bold text-slate-500">
                Saved on this device only.
              </p>
            </div>

            {/* Footer actions — sticky bottom of the card. */}
            <div className="border-t-4 border-slate-900 bg-slate-50 px-3 py-2 flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={close}
                data-testid="born-in-skip-btn"
                className="px-3 py-1.5 rounded-xl border-[3px] border-slate-900 bg-white font-bold text-xs shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-all whitespace-nowrap"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!name.trim() && !selectedCode}
                data-testid="born-in-submit"
                className="flex-1 px-3 py-1.5 rounded-xl border-[3px] border-slate-900 bg-emerald-300 font-bold text-sm shadow-[0_4px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_3px_0_0_rgba(15,23,42,1)] active:translate-y-[2px] active:shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[0_4px_0_0_rgba(15,23,42,1)] whitespace-nowrap"
              >
                Start exploring 🌍
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
